import { useCallback, useState } from 'react';
import * as DocumentPicker from 'expo-document-picker';
import { nanoid as uuid } from 'nanoid';

import { SelectedMedia } from '@pletly/common';
import { MAX_MEDIA_COUNT, SUPPORTED_DOCUMENT_FORMATS, validateFilesSize } from '@pletly/shared';
import { Toast, showErrorToUser } from '@pletly/ui';

export interface SelectedFile extends Omit<SelectedMedia, 'base64'> {
	uuid: string;
}

export const usePickedFiles = (
	initialState: SelectedFile[] = [],
	options?: DocumentPicker.DocumentPickerOptions & { maxFiles?: number },
) => {
	const [pickedFiles, setFiles] = useState<SelectedFile[]>(initialState);

	const pickFiles = async () => {
		const pickerResult = await DocumentPicker.getDocumentAsync({
			multiple: true,
			type: SUPPORTED_DOCUMENT_FORMATS,
			...options,
		});

		if (pickerResult.canceled === true) {
			return;
		}

		const maxFiles = options?.maxFiles || MAX_MEDIA_COUNT;
		if (pickedFiles.length + pickerResult.assets.length > maxFiles) {
			return Toast.show({
				text1: `Max ${maxFiles} files`,
				type: 'error',
			});
		}

		try {
			validateFilesSize(pickerResult.assets);
		} catch (error) {
			return showErrorToUser(error as Error);
		}

		setFiles((previouslySelectedFiles) => [
			...previouslySelectedFiles,
			...pickerResult.assets.map((file) => ({ ...file, uuid: uuid() })),
		]);
	};

	const onRemoveFile = useCallback((index: number) => {
		setFiles((files) => files.filter((_, i) => i !== index));
	}, []);

	return { pickedFiles, pickFiles, onRemoveFile };
};
